import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

// Flags generation-affecting grid changes (cell toggles + staged row/column deletes)
// so Save warns, and renders the "staged for removal" dimming for rows and columns.
export default class extends Controller {
  static targets = ["alert", "flag"]

  connect() {
    this.watched = Array.from(this.element.querySelectorAll("[data-matrix-regen]"))
    this.initial = new Map(this.watched.map((el) => [el, this.stateOf(el)]))
    this.evaluate = this.evaluate.bind(this)
    this.element.addEventListener("input", this.evaluate)
    this.element.addEventListener("change", this.evaluate)
    this.restage()
    this.evaluate()
  }

  disconnect() {
    this.element.removeEventListener("input", this.evaluate)
    this.element.removeEventListener("change", this.evaluate)
  }

  toggleRowDelete(event) {
    this.flip(event.currentTarget, event.currentTarget.closest("tr")?.querySelector("input[name='delete_row_ids[]']"))
  }

  toggleColDelete(event) {
    this.flip(event.currentTarget, event.currentTarget.closest("th")?.querySelector("input[name='delete_col_ids[]']"))
  }

  flip(button, checkbox) {
    if (!checkbox) return

    const marked = !checkbox.checked
    checkbox.checked = marked

    button.classList.toggle("!text-error-600", marked)
    button.title = marked ? i18n.t("js.matrix_regen.undo_remove") : (button.dataset.removeTitle || i18n.t("js.common.remove"))
    const icon = button.querySelector("i")
    if (icon) icon.className = marked ? "fa-solid fa-rotate-left" : "fa-solid fa-trash"

    this.restage()
    this.evaluate()
  }

  // Recompute all staged-for-removal styling wholesale so intersecting row/column
  // stages stay correct (un-staging a row still dims cells whose column is staged).
  restage() {
    const table = this.element.querySelector("table")
    if (!table) return

    const head = table.tHead && table.tHead.rows[0]
    const colStaged = []
    if (head) {
      Array.from(head.cells).forEach((th, i) => {
        const cb = th.querySelector("input[name='delete_col_ids[]']")
        const staged = !!(cb && cb.checked)
        colStaged[i] = staged
        th.classList.toggle("opacity-50", staged)
        th.querySelectorAll("input[type='text']").forEach((input) => {
          input.disabled = staged
          input.classList.toggle("line-through", staged)
        })
      })
    }

    const body = table.tBodies[0]
    if (!body) return
    Array.from(body.rows).forEach((tr) => {
      const cb = tr.querySelector("input[name='delete_row_ids[]']")
      if (!cb) return // skip the "add row" footer row (no delete control)
      const rowStaged = cb.checked
      tr.classList.toggle("opacity-50", rowStaged)
      tr.querySelectorAll("input[type='text']").forEach((input) => {
        input.disabled = rowStaged
        input.classList.toggle("line-through", rowStaged)
      })
      // Dim cells whose column is staged (a staged row is already dimmed via the <tr>).
      Array.from(tr.cells).forEach((td, i) => {
        td.classList.toggle("opacity-50", !rowStaged && !!colStaged[i])
      })
    })
  }

  stateOf(el) {
    return el.type === "checkbox" ? String(el.checked) : el.value
  }

  evaluate() {
    const cellsDirty = this.watched.some((el) => this.initial.get(el) !== this.stateOf(el))
    const deletesStaged = this.element.querySelectorAll(
      "input[name='delete_row_ids[]']:checked, input[name='delete_col_ids[]']:checked"
    ).length > 0
    const dirty = cellsDirty || deletesStaged

    if (this.hasAlertTarget) this.alertTarget.classList.toggle("hidden", !dirty)
    if (this.hasFlagTarget) this.flagTarget.value = dirty ? "1" : "0"
  }
}
